/**
 * RNR's Radio Group — one of a small fixed set, chosen by tapping the thing itself.
 *
 * The registry component is a column of ringed dots with labels beside them. The app's one use is
 * the Pulse face scale, where the FACE is the radio: five of them in a row, and the chosen one
 * takes a ring while the rest drop to 40%. So `RadioGroupItem` here is a target with no dot of its
 * own — the caller draws what is being chosen and this owns choosing it.
 *
 * That is the whole reason to reach for the primitive rather than a Pressable row: it puts
 * `role="radio"` and the checked state on every item and `role="radiogroup"` on the set, so a
 * screen reader announces "3 of 5, Fine, selected" rather than five unrelated buttons. Selecting
 * one deselects the others without this file holding any state.
 */
import { cn } from '@/lib/utils';
import * as RadioGroupPrimitive from '@rn-primitives/radio-group';
import { Platform } from 'react-native';

function RadioGroup({ className, ...props }: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return <RadioGroupPrimitive.Root className={cn('flex-row gap-1.5', className)} {...props} />;
}

/**
 * One choice. The 44px minimum is the target, not the ornament: the boards draw the faces at 52
 * and 60px, and a scale whose smallest hit area is smaller than a fingertip is a scale that
 * records the wrong number.
 */
function RadioGroupItem({ className, ...props }: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      className={cn(
        'min-h-11 min-w-11 items-center justify-center',
        Platform.select({ web: 'cursor-default disabled:pointer-events-none' }),
        props.disabled && 'opacity-50',
        className,
      )}
      {...props}
    />
  );
}

export { RadioGroup, RadioGroupItem };
